<template>
  <StepCard
    step-id="set_member_payment_details"
    :description="t('description')"
    :edit-label="t('edit')"
    :completed
    :errors="serverErrors"
    :saving
    :save-label="t('save')"
    :save-hint
    @save="save"
    @skip="$emit('skip')"
    @cancel="$emit('cancel')"
  >
    <template #summary>
      <ZoneTable
        :columns="summaryColumns"
        :rows="summaryRows"
        row-key="key"
        :toolbar="false"
        :paginated="false"
        sticky-header
        :label="t('step_names.set_member_payment_details')"
        data-testid="payment-summary"
      >
        <template #cell-member="{ row }">
          <div class="flex items-center gap-3">
            <ZoneAvatar
              :src="row.avatar"
              :name="row.name || row.email"
              alt=""
            />
            <div class="min-w-0">
              <p class="truncate text-sm font-medium text-gray-900">{{ row.name || row.email }}</p>
              <p
                v-if="row.name"
                class="truncate text-xs text-gray-500"
              >
                {{ row.email }}
              </p>
            </div>
          </div>
        </template>
        <template
          v-for="field in summaryFields"
          :key="field"
          #[`cell-${field}`]="{ value }"
        >
          <span
            class="whitespace-nowrap"
            :class="value.muted ? 'text-gray-500' : 'text-gray-900'"
          >{{ value.text }}</span>
        </template>
      </ZoneTable>
    </template>

    <ZoneTable
      :columns
      :rows
      row-key="key"
      :toolbar="false"
      :paginated="false"
      sticky-header
      :label="t('step_names.set_member_payment_details')"
      data-testid="payment-table"
    >
      <template #cell-email="{ row }">
        <div class="flex items-center gap-3">
          <ZoneAvatar
            :src="row.avatar"
            :name="row.name || row.email"
            alt=""
          />
          <div class="min-w-0">
            <p class="truncate text-sm font-medium text-gray-900">{{ row.name || row.email }}</p>
            <p
              v-if="row.name"
              class="truncate text-xs text-gray-500"
            >
              {{ row.email }}
            </p>
          </div>
        </div>
      </template>
      <template #header-actions="{ column }">
        <span class="sr-only">{{ column.label }}</span>
      </template>
      <template #cell-details="{ value }">
        <span :class="{ 'text-gray-900': value }">{{ value || t('not_set') }}</span>
      </template>
      <template #cell-actions="{ row }">
        <ZoneButton
          size="sm"
          variant="outline"
          color="gray_alt"
          class="whitespace-nowrap hover:bg-gray-100!"
          :data-testid="`payment-edit-${row.key}`"
          @click="openEditor(row)"
        >
          {{ t('set_details') }}
        </ZoneButton>
      </template>
    </ZoneTable>

    <template #dialog>
      <ZoneModal
        v-model="editorOpen"
        :title="t('dialog_title')"
        width="48rem"
        data-testid="payment-dialog"
      >
        <form
          v-if="selected"
          :id="editorFormId"
          ref="editorForm"
          class="flex flex-col gap-4"
          novalidate
          @submit="applyEditor"
        >
          <div>
            <p class="text-xs font-medium uppercase text-gray-600">{{ t('member') }}</p>
            <p class="mt-1 text-sm text-gray-900">{{ selected.email }}</p>
          </div>
          <ZoneTextInput
            v-model="amount"
            inputmode="decimal"
            :label="t(selected.fixed ? 'fixed_amount' : 'pay_rate')"
            placeholder="0.00"
            :invalid="!!errors.amount"
            data-testid="payment-rate"
          >
            <template #notice>
              <p
                v-if="errors.amount"
                class="text-xs text-red-600"
                role="alert"
                data-testid="payment-rate-error"
              >
                {{ errors.amount }}
              </p>
              <p
                v-else
                class="text-xs text-gray-400"
              >
                {{ t(selected.fixed ? 'fixed_unit' : 'rate_unit') }}
              </p>
            </template>
          </ZoneTextInput>
          <PayPeriodCustomDatesSelector
            :member="selected"
            :member-initial-state="selectedInitial"
            :pay-period-options="MEMBERSHIP_PAY_PERIOD_OPTIONS"
            class="mx-0! -mb-4! [&_.col-item]:float-none! [&_.col-item]:mb-4! [&_.col-item]:w-full! [&_.col-item]:px-0! [&_.btn-group-spaced]:-ml-2.5! [&_.pay-period-info-alert-wrapper]:mx-0! [&_.pay-period-info-alert-wrapper]:mt-0! [&_.pay-period-info-alert-wrapper]:mb-4! [&_.pay-period-info-alert-wrapper]:px-0! [&_.pay-period-label]:mb-2! [&_.pay-period-label]:text-xs! [&_.pay-period-label]:text-gray-400!"
            @input="onCustomPeriodInput"
          />
          <p
            v-if="errors.pay_period"
            class="-mt-3 text-xs text-red-600"
            role="alert"
            data-testid="payment-period-error"
          >
            {{ errors.pay_period }}
          </p>
          <template v-if="data.timesheet_approvals_available">
            <div class="self-start">
              <ZoneTooltip :content="approvalLocked ? t('period_required') : null">
                <ZoneToggle
                  v-model="timesheetsEnabled"
                  size="small"
                  :disabled="approvalLocked"
                  :state-text="t('require_approval')"
                  data-testid="payment-approval"
                />
              </ZoneTooltip>
            </div>
            <ZoneAlert
              v-if="approvalTurningOff"
              color="red"
              size="sm"
              icon="warning"
            >
              {{ t('approval_warning') }}
            </ZoneAlert>
          </template>
        </form>
        <template #actions="{ close }">
          <ZoneButton
            variant="outline"
            color="gray_alt"
            data-testid="modal-cancel"
            @click="close"
          >
            {{ t('cancel') }}
          </ZoneButton>
          <ZoneButton
            type="submit"
            color="primary_alt"
            :form="editorFormId"
            :disabled="!meta.valid"
            :tooltip="meta.valid ? null : t('fix_errors')"
            data-testid="modal-confirm"
          >
            {{ t('dialog_save') }}
          </ZoneButton>
        </template>
      </ZoneModal>
    </template>
  </StepCard>
</template>

<script setup>
import { pick } from 'lodash-es';
import { useForm } from 'vee-validate';
import { computed, provide, ref, useId, useTemplateRef } from 'vue';
import { useI18n } from 'vue-i18n';
import StepCard from '~/components/experiments/0239_getting_started_checklist/StepCard.vue';
import { useStepSave } from '~/components/experiments/0239_getting_started_checklist/useStepSave';
import { InvitesResource, UserOrganizationsResource } from '~/resources';
import { SUMMARY_COLUMN, SUMMARY_VALUE_COLUMN } from '~/components/experiments/0239_getting_started_checklist/summaryTable';
import ZoneAlert from 'zone/components/base/Alert.vue';
import ZoneAvatar from 'zone/components/base/Avatar.vue';
import ZoneButton from 'zone/components/base/Button.vue';
import ZoneModal from 'zone/components/base/Modal.vue';
import ZoneTable from 'zone/components/base/Table.vue';
import ZoneTextInput from 'zone/components/base/TextInput.vue';
import ZoneToggle from 'zone/components/base/Toggle.vue';
import ZoneTooltip from 'zone/components/base/Tooltip.vue';
import { MEMBERSHIP_PAY_PERIOD_OPTIONS, RATE_LIMIT } from '~/app_constants';
import PayPeriodCustomDatesSelector from '~/components/shared/PayPeriodCustomDatesSelector.vue';
import { validatePayPeriodState } from '~/utils/validate_pay_period_state';
import { chain, maxValue, minValue, numeric } from '~/zone_inertia/validations';

const PAY_PERIODS = ['weekly', 'twice_per_month', 'biweekly', 'monthly'];
const FIXED_RATE_TYPE = 1;
const PERIOD_DATE_FIELDS = ['period_week_day', 'period_start_day', 'period_second_start_day', 'period_anchor_date'];
const PERIOD_DATE_FIELDS_BY_PERIOD = {
  weekly: ['period_week_day'],
  twice_per_month: ['period_start_day', 'period_second_start_day'],
  biweekly: ['period_week_day', 'period_anchor_date'],
  monthly: ['period_start_day'],
};
const SUMMARY_FIELDS = ['rate', 'period', 'approval'];
const HEADER_CLASS = 'border-r-0! px-4! py-3! text-xs! font-medium! uppercase text-gray-500!';
const CELL_CLASS = 'border-r-0! px-4! py-3! font-regular! text-gray-500! [tr:last-child_&]:border-b-0!';

const { data, completed, organization } = defineProps({
  data: { type: Object, required: true },
  completed: { type: Boolean },
  organization: { type: Object, required: true },
});

const emit = defineEmits(['saved', 'skip', 'cancel']);

const { t } = useI18n();
const { saving, serverErrors, failures, serverMessages, finishStep, run } = useStepSave(emit);

const drafts = ref({});
const selected = ref(null);
const selectedInitial = ref(null);
const editorFormId = useId();

const editorForm = useTemplateRef('editorForm');

provide('selectDropdownParent', computed(() => editorForm.value && $(editorForm.value)));

const blank = (value) => value === '' || value == null;

const { errors, meta, defineField, handleSubmit, resetForm, setFieldError } = useForm({
  validationSchema: {
    amount: chain([numeric(), minValue(0), maxValue(RATE_LIMIT)]),
  },
});
const [amount] = defineField('amount');
const [payPeriod] = defineField('pay_period');
const [timesheetsEnabled] = defineField('timesheets_enabled');

const payPeriodOptions = computed(() => PAY_PERIODS.map((period, id) => ({ id, name: t(`pay_periods.${period}`) })));
const members = computed(() => [
  ...data.users.map((user) => {
    const fixed = user.rate_type === FIXED_RATE_TYPE;

    return { ...user, fixed, amount: fixed ? user.fixed_amount : user.pay_rate, type: 'user', key: `user-${user.id}` };
  }),
  ...data.invites.map((invite) => ({ ...invite, amount: invite.pay_rate, type: 'invite', key: `invite-${invite.id}` })),
]);
const rows = computed(() => members.value.map((member) => {
  const row = drafts.value[member.key] ?? member;
  const period = payPeriodOptions.value.find((option) => option.id === row.pay_period);
  const rate = blank(row.amount) ? null : Number(row.amount).toFixed(2);

  return { ...row, details: rate && t(row.fixed ? 'fixed_details' : 'details', { rate, period: period?.name ?? t('none') }) };
}));
const paymentState = (member) => JSON.stringify([
  blank(member.amount) ? null : Number(member.amount),
  blank(member.pay_period) ? null : Number(member.pay_period),
  !!member.timesheets_enabled,
  ...(PERIOD_DATE_FIELDS_BY_PERIOD[PAY_PERIODS[member.pay_period]] ?? []).map((field) => member[field] ?? null),
]);
const edited = computed(() => members.value.filter((member) => drafts.value[member.key] && paymentState(drafts.value[member.key]) !== paymentState(member)).map((member) => drafts.value[member.key]));

const save = () => run(async () => {
  const results = await Promise.allSettled(edited.value.map((member) => (member.type === 'invite'
    ? InvitesResource.updatePayRate(
      { id: member.id },
      {
        pay_rate: member.amount ? parseFloat(member.amount) : null,
        pay_period: member.pay_period,
        timesheets_enabled: member.timesheets_enabled ? 1 : 0,
        ...pick(member, PERIOD_DATE_FIELDS),
      },
    )
    : UserOrganizationsResource.updatePaymentInfo(
      { id: member.id },
      {
        user_organization: {
          [member.fixed ? 'fixed_amount' : 'pay_rate']: member.amount,
          pay_period: member.pay_period,
          timesheets_enabled: member.timesheets_enabled,
          ...pick(member, PERIOD_DATE_FIELDS),
        },
      },
    ))));

  const messages = [...failures(results), ...results.filter(({ value }) => value && !value.success).flatMap(({ value }) => serverMessages(value))];

  return messages.length ? messages : finishStep(organization.id, 'set_member_payment_details');
});
const columns = computed(() => [
  { key: 'email', label: t('email') },
  { key: 'details', label: t('payment_details') },
  { key: 'actions', label: t('actions'), align: 'right' },
].map((column) => ({ ...column, headerClass: HEADER_CLASS, cellClass: CELL_CLASS })));
const summaryFields = computed(() => SUMMARY_FIELDS.filter((field) => field !== 'approval' || data.timesheet_approvals_available));
const summaryColumns = computed(() => [
  { key: 'member', label: t('member'), ...SUMMARY_COLUMN },
  ...summaryFields.value.map((field) => ({ key: field, label: t(`summary.${field}`), ...SUMMARY_VALUE_COLUMN })),
]);
const summaryRows = computed(() => members.value.map((member) => {
  if (blank(member.amount)) {
    return { ...member, rate: { text: t('not_set'), muted: true }, period: { text: '' }, approval: { text: '' } };
  }

  const period = payPeriodOptions.value.find((option) => option.id === member.pay_period);

  return {
    ...member,
    rate: { text: t(member.fixed ? 'fixed_rate' : 'rate', { rate: Number(member.amount).toFixed(2) }) },
    period: period ? { text: period.name } : { text: t('none'), muted: true },
    approval: member.timesheets_enabled ? { text: t('required') } : { text: t('off'), muted: true },
  };
}));
const saveHint = computed(() => (edited.value.length === 0 ? t('save_hint') : null));
const approvalLocked = computed(() => blank(payPeriod.value));
const approvalTurningOff = computed(() => selectedInitial.value?.timesheets_enabled && !timesheetsEnabled.value && !approvalLocked.value);
const editorOpen = computed({
  get: () => !!selected.value,
  set: (open) => { if (!open) {selected.value = null;} },
});

const openEditor = (row) => {
  selected.value = { ...row };
  selectedInitial.value = { ...row };
  resetForm({
    values: { amount: row.amount ?? '', pay_period: row.pay_period ?? '', timesheets_enabled: !!row.timesheets_enabled },
  });
};

const onCustomPeriodInput = (state) => {
  payPeriod.value = state.payPeriodId === '' ? '' : Number(state.payPeriodId);

  if (blank(payPeriod.value)) {
    timesheetsEnabled.value = false;
  }

  selected.value = {
    ...selected.value,
    pay_period: payPeriod.value,
    _previousPayPeriodId: state.previousPayPeriodId,
    period_week_day: state.periodWeekDay,
    period_start_day: state.periodStartDay,
    period_second_start_day: state.periodSecondStartDay,
    period_anchor_date: state.periodAnchorDate,
  };
};

const applyEditor = handleSubmit((values) => {
  const invalid = validatePayPeriodState({
    payPeriodId: values.pay_period,
    periodWeekDay: selected.value.period_week_day,
    periodStartDay: selected.value.period_start_day,
    periodSecondStartDay: selected.value.period_second_start_day,
    periodAnchorDate: selected.value.period_anchor_date,
  });

  if (invalid) {
    setFieldError('pay_period', invalid);

    return;
  }

  drafts.value = { ...drafts.value, [selected.value.key]: { ...selected.value, ...values } };
  selected.value = null;
});
</script>

<i18n lang="json" src="~/components/experiments/0239_getting_started_checklist/steps.json"></i18n>

<i18n lang="json">
{
  "en": {
    "description": "Set pay rates and pay periods for your team. You can also set timesheets to require approval before payments are sent.",
    "email": "Email",
    "payment_details": "Payment details",
    "actions": "Actions",
    "not_set": "Details not set",
    "details": "{rate} USD/hr / {period}",
    "fixed_details": "{rate} USD fixed / {period}",
    "rate": "{rate} USD/hr",
    "fixed_rate": "{rate} USD fixed",
    "required": "Required",
    "off": "Off",
    "summary": {
      "rate": "Pay rate",
      "period": "Pay period",
      "approval": "Timesheet approval"
    },
    "set_details": "Edit details",
    "dialog_title": "Edit payment details",
    "dialog_save": "Save",
    "member": "Member",
    "pay_rate": "Pay rate",
    "rate_unit": "USD/hr",
    "fixed_amount": "Fixed amount",
    "fixed_unit": "USD per pay period",
    "period_required": "Pick a pay period to require timesheet approval",
    "pay_periods": {
      "weekly": "Weekly",
      "twice_per_month": "Twice per month",
      "biweekly": "Bi-weekly",
      "monthly": "Monthly"
    },
    "require_approval": "Timesheet generation & approval",
    "approval_warning": "This will delete any open, submitted, and denied timesheets and will stop generating future timesheets for this user. Approved or paid timesheets will not be affected.",
    "save": "Save payment details",
    "save_hint": "Set one member's payment details first.",
    "edit": "Edit payment details"
  }
}
</i18n>
